import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  TabList,
  Tab,
  makeStyles,
  Text,
  Card
} from "@fluentui/react-components";
import { FormattedMessage } from "react-intl";
import { CloseButton } from "../input/CloseButton";
import { eventScheduleDayKey, formatEventScheduleDay, formatEventScheduleTime } from "../../utils/events-utils";

const useStyles = makeStyles({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "5px"
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  card: {
    backgroundColor: "var(--primaryColorBackground)",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    cursor: "pointer"
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    flex: 1
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  time: {
    color: "var(--Gray-4, #BDBDBD)"
  },
  capacity: {
    color: "var(--Gray-4, #BDBDBD)"
  },
  speakers: {
    color: "var(--Gray-3, #828282)"
  },
  title: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "600"
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "8px"
  },
  selectAll: {
    marginTop: "8px",
    marginLeft: "10px"
  }
});

export const PresentationsSelectDialog = ({ open, onClose, presentations, preselectedSlug, onConfirm }) => {
  const classes = useStyles();

  const sorted = useMemo(
    () =>
      (presentations || [])
        .filter(p => p?.start_time || p?.start_time_next)
        .slice()
        .sort((a, b) => new Date(a.start_time || a.start_time_next) - new Date(b.start_time || b.start_time_next)),
    [presentations]
  );

  const days = useMemo(
    () => Array.from(new Set(sorted.map(p => eventScheduleDayKey(p.start_time || p.start_time_next, p.timezone)))),
    [sorted]
  );
  const dayLabels = useMemo(
    () =>
      new Map(
        sorted.map(p => [
          eventScheduleDayKey(p.start_time || p.start_time_next, p.timezone),
          formatEventScheduleDay(p.start_time || p.start_time_next, p.timezone)
        ])
      ),
    [sorted]
  );

  // Find the day that contains the preselected presentation
  const preselectedDay = useMemo(() => {
    if (!preselectedSlug || !sorted.length) return null;
    const preselectedPresentation = sorted.find(p => p.slug === preselectedSlug);
    return preselectedPresentation
      ? eventScheduleDayKey(
          preselectedPresentation.start_time || preselectedPresentation.start_time_next,
          preselectedPresentation.timezone
        )
      : null;
  }, [preselectedSlug, sorted]);

  const [currentDay, setCurrentDay] = useState(preselectedDay || days[0] || "");

  const listForDay = useMemo(
    () => sorted.filter(p => eventScheduleDayKey(p.start_time || p.start_time_next, p.timezone) === currentDay),
    [sorted, currentDay]
  );

  const initial = useMemo(() => (preselectedSlug ? new Set([preselectedSlug]) : new Set()), [preselectedSlug]);
  const [selected, setSelected] = useState(initial);

  const toggle = slug => {
    const next = new Set(selected);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    setSelected(next);
  };

  const allSelectableSlugs = useMemo(
    () => listForDay.filter(p => (p.attendees_count ?? 0) < (p.capacity ?? 50)).map(p => p.slug),
    [listForDay]
  );

  const onSelectAll = (e, data) => {
    const next = new Set(selected);
    if (data.checked) {
      for (const slug of allSelectableSlugs) next.add(slug);
    } else {
      for (const slug of allSelectableSlugs) next.delete(slug);
    }
    setSelected(next);
  };

  // Calculate the state of the "Select All" checkbox
  const selectAllChecked = useMemo(() => {
    const selectedSelectableSlugs = allSelectableSlugs.filter(slug => selected.has(slug));
    const allSelected = selectedSelectableSlugs.length === allSelectableSlugs.length && allSelectableSlugs.length > 0;
    const someSelected =
      selectedSelectableSlugs.length > 0 && selectedSelectableSlugs.length < allSelectableSlugs.length;

    return allSelected ? true : someSelected ? "mixed" : false;
  }, [selected, allSelectableSlugs]);

  const canContinue = selected.size > 0;

  return (
    <Dialog open={open} onOpenChange={onClose} modalType="modal">
      <DialogSurface>
        <DialogBody>
          <DialogTitle action={onClose ? <CloseButton onClick={onClose} /> : null}>
            <FormattedMessage id="presentations-select.title" defaultMessage="Select Presentation" />
          </DialogTitle>
          <DialogContent className={classes.content}>
            <Text>
              <FormattedMessage
                id="presentations-select.subtitle"
                defaultMessage="Select the presentations you wish to attend at this activation. You must select at least one presentation to continue."
              />
            </Text>
            <TabList selectedValue={currentDay} onTabSelect={(e, d) => setCurrentDay(d.value)}>
              {days.map(key => (
                <Tab key={key} value={key}>
                  {dayLabels.get(key)}
                </Tab>
              ))}
            </TabList>

            <div className={classes.list}>
              {listForDay.map(p => {
                const total = p.capacity_total ?? p.capacity ?? p.capacity_max ?? 50;
                const used = p.capacity_used ?? p.used_capacity ?? p.attendees_count ?? 0;
                const soldOut = used >= total;
                const start = formatEventScheduleTime(p.start_time_next || p.start_time, p.timezone);
                const checked = selected.has(p.slug);

                return (
                  <div key={p.slug} className={classes.row} style={{ opacity: soldOut ? 0.4 : 1 }}>
                    <Card
                      className={classes.card}
                      onClick={() => {
                        if (!soldOut) toggle(p.slug);
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={soldOut}
                        onClick={e => e.stopPropagation()}
                        onChange={() => toggle(p.slug)}
                      />
                      <div className={classes.cardContent}>
                        <div className={classes.header}>
                          <p className={classes.time}>{start}</p>
                          <p className={classes.capacity}>
                            <FormattedMessage id="presentations-select.capacity" defaultMessage="Capacity" />: {used}/
                            {total}
                          </p>
                        </div>
                        <p className={classes.title}>{p.title || p.name}</p>
                        {p.presenters?.length > 0 && (
                          <p className={classes.speakers}>{p.presenters.map(x => x.name).join(", ")}</p>
                        )}
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>

            <div className={classes.selectAll}>
              <Checkbox
                checked={selectAllChecked}
                onChange={onSelectAll}
                label={
                  <FormattedMessage id="presentations-select.select-all" defaultMessage="Select all presentations" />
                }
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              <FormattedMessage id="presentations-select.cancel" defaultMessage="Cancel" />
            </Button>
            <Button appearance="primary" disabled={!canContinue} onClick={() => onConfirm(Array.from(selected))}>
              <FormattedMessage id="presentations-select.continue" defaultMessage="Continue" />
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

PresentationsSelectDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  presentations: PropTypes.array,
  preselectedSlug: PropTypes.string,
  onConfirm: PropTypes.func
};

export default PresentationsSelectDialog;
